import FormComponent from '../FormComponent';
import SelectorEngine from '../../utils/dom/selector-engine';
import EventHandler from '../../utils/dom/event-handler';
import { ClassNames } from '../../utils/dom/css-classes';

const CHANGE_EVENT = 'change';

class CheckboxComponent extends FormComponent {
	constructor(containerElement, config) {
		super(containerElement, config);
		this.classPrefix = 'lta-form-check';
		this.errorMessage = config.data.errorMessage || 'Select at least one option';
		this.data.minSelectable = config.data.minSelectable >= 0 ? config.data.minSelectable : 1;
		this.data.maxSelectable = config.data?.maxSelectable;
		this.minSelectableErrorMessage =
			config.data.minSelectableErrorMessage || `Minimum ${this.data.minSelectable} options should be selected`;
		this.maxSelectableErrorMessage =
			config.data.maxSelectableErrorMessage || `Maximum ${this.data.maxSelectable} options allowed`;
		this.selectedOptions = config?.data?.value || [];

		// New: Select All configuration
		this.hasSelectAll = config.data?.hasSelectAll !== false;

		if (this.data?.value) {
			this.data.value = this.data?.value?.split(',') || [];
		}
		this.initEventListeners();
		setTimeout(() => {
			this.handleCheckboxClick(false);
			this.updateSelectAllState();
		}, 0);
	}

	markup(value, data, titleMarkup) {
		this.selectAllId = `${data.id}_select_all`;
		const spacingClass = data?.spacingClass || 'u-mb-5';

		return `
			<fieldset>
				<legend>${titleMarkup}</legend>
				${
					data?.instructionText
						? `<p class="lta-text-input__label u-text-grey-lta lta-heading-subtitle">${data?.instructionText}</p>`
						: ''
				}
				<div class="lta-form-check-group ${spacingClass} ">
					<div id="lta-radio-checkbox-error" class="lta-form-check--error u-mb-1 u-hidden">${data.errorMessage}</div>
					<div class="lta-form-check--options lta-form-checkbox">
						${data.hasSelectAll ? this.renderSelectAllOption() : ''}
						${data.options
							.map((option) => {
								const checkboxId = `${data.id}_${option.id}`;
								const isMandatory = option.isMandatory === true;
								const isChecked = this.data.value?.includes(option.id) || isMandatory;
								const labelMarkup = option.description
									? `<div class='u-flex' style="flex-direction:column">
									<label class="lta-form-check-label u-body1" for="${checkboxId}">${option.label}</label>
									<label class="lta-form-lozenge-label u-body2" for="${checkboxId}">${option.description}</label>
								</div>`
									: `<label class="lta-form-check-label u-body1" for="${checkboxId}">${option.label}</label>`;

								return `
							<div class="lta-form-check--option lta-form-lozenge u-mb-1">
								<input class="lta-form-check-input u-mr-2 js-option-checkbox" type="checkbox" id="${checkboxId}" value="${option.id}" ${
									isChecked ? 'checked' : ''
								} ${option.isDisabled || isMandatory ? 'disabled' : ''} />
								${labelMarkup}
							</div>
						`;
							})
							.join('')}
					</div>
				</div>
			</fieldset>
		`;
	}

	renderSelectAllOption() {
		return `
			<div class="lta-form-check--select-all-wrapper u-mb-3 u-mt-3">
				<div class="lta-form-check--option u-mb-3" style="margin-left: 0;">
					<input class="lta-form-check-input u-mr-2 js-select-all-checkbox" type="checkbox" id="${this.selectAllId}" />
					<label class="lta-form-check-label u-body1" for="${this.selectAllId}">Select all</label>
				</div>
				<div class="lta-form-check--separator u-mb-3" style="border-bottom: 1px solid #e0e0e0;"></div>
			</div>
		`;
	}

	afterRender() {
		// Initialize select all state based on current selections
		if (this.hasSelectAll) {
			this.updateSelectAllState();
		}
	}

	initEventListeners() {
		const checkboxes = SelectorEngine.find('input[type="checkbox"]', this.containerEl);

		for (const checkbox of checkboxes) {
			EventHandler.on(checkbox, CHANGE_EVENT, (event) => {
				if (checkbox.classList.contains('js-select-all-checkbox')) {
					this.handleSelectAllChange(event);
				} else {
					this.handleCheckboxClick(true);
					this.updateSelectAllState();
				}
			});
		}
	}

	handleSelectAllChange(event) {
		const selectAllCheckbox = event.target;
		const optionCheckboxes = SelectorEngine.find('.js-option-checkbox', this.containerEl);

		for (const checkbox of optionCheckboxes) {
			if (checkbox.disabled) {
				continue;
			}
			if (checkbox.checked !== selectAllCheckbox.checked) {
				checkbox.checked = selectAllCheckbox.checked;
			}
		}

		this.handleCheckboxClick(true);
	}

	updateSelectAllState() {
		if (!this.hasSelectAll) return;

		const selectAllCheckbox = SelectorEngine.findOne('.js-select-all-checkbox', this.containerEl);
		if (!selectAllCheckbox) return;

		const optionCheckboxes = SelectorEngine.find('.js-option-checkbox', this.containerEl);
		const enabledCheckboxes = optionCheckboxes.filter((checkbox) => !checkbox.disabled);
		const allChecked = enabledCheckboxes.length > 0 && enabledCheckboxes.every((checkbox) => checkbox.checked);

		selectAllCheckbox.checked = allChecked;
	}

	handleCheckboxClick(shouldValidate = true) {
		const checkboxes = SelectorEngine.find('.js-option-checkbox', this.containerEl);
		this.checkedCheckboxes = checkboxes.filter((checkbox) => checkbox.checked);

		let isValid = true;
		if (shouldValidate) {
			isValid = this.validate();
		}
		if (isValid) this.updateValue(this.checkedCheckboxes);
	}

	validate() {
		const errorElement = SelectorEngine.findOne('#lta-radio-checkbox-error', this.containerEl);
		const inputGroupElement = SelectorEngine.findOne('.lta-form-check-group', this.containerEl);

		const checkboxes = SelectorEngine.find('.js-option-checkbox', this.containerEl);
		this.checkedCheckboxes = checkboxes.filter((checkbox) => checkbox.checked);
		const mutuallyExclusiveError = this.getMutuallyExclusiveError();
		if (mutuallyExclusiveError) {
			errorElement.innerHTML = mutuallyExclusiveError;
			errorElement.classList.remove(ClassNames.Hidden);
			inputGroupElement.classList.add(ClassNames.Invalid, ClassNames.Focused);
			return false;
		}

		if (
			!this.checkedCheckboxes ||
			(this.checkedCheckboxes.length < this.data?.minSelectable && !this.data.isOptional)
		) {
			errorElement.innerHTML = this.checkedCheckboxes.length === 0 ? this.errorMessage : this.minSelectableErrorMessage;
			errorElement.classList.remove(ClassNames.Hidden);
			inputGroupElement.classList.add(ClassNames.Invalid, ClassNames.Focused);
			return false;
		} else if (
			!this.checkedCheckboxes ||
			(this.checkedCheckboxes.length > this.data?.maxSelectable && !this.data.isOptional)
		) {
			errorElement.classList.remove(ClassNames.Hidden);
			errorElement.innerHTML = this.maxSelectableErrorMessage;
			inputGroupElement.classList.add(ClassNames.Invalid, ClassNames.Focused);
			return false;
		} else {
			errorElement.classList.add(ClassNames.Hidden);
			inputGroupElement.classList.remove(ClassNames.Invalid, ClassNames.Focused);
			return true;
		}
	}

	getMutuallyExclusiveError() {
		const rules = this.data?.mutuallyExclusiveRules || [];
		if (!rules.length || !this.checkedCheckboxes?.length) {
			return null;
		}

		const selectedIds = new Set(this.checkedCheckboxes.map((checkbox) => checkbox.value));

		for (const rule of rules) {
			const groupA = rule?.groupA || [];
			const groupB = rule?.groupB || [];
			if (!groupA.length || !groupB.length) {
				continue;
			}

			const hasA = groupA.some((id) => selectedIds.has(id));
			const hasB = groupB.some((id) => selectedIds.has(id));

			if (hasA && hasB) {
				return rule.errorMessage || 'These options cannot be selected at the same time';
			}
		}

		return null;
	}

	updateValue(checkedCheckboxes = []) {
		const getSelectedCheckboxData = (selectedCheckbox) =>
			this?.data.options?.find((option) => option.id == selectedCheckbox.value);

		const selectedCheckboxesData = checkedCheckboxes.map((selectedCheckbox) =>
			getSelectedCheckboxData(selectedCheckbox)
		);

		this.value = {
			id: this.data.id,
			fieldName: this.data.fieldName,
			value: selectedCheckboxesData || []
		};

		// NOTE: Currently only supporting one dependent question per checkbox item
		this.dependentQuestion = selectedCheckboxesData.map(({ dependentQuestion }) => dependentQuestion?.[0]);
	}
}

export default CheckboxComponent;
